'use client'

import { Button } from '@/components/ui/button'
import { MembershipLevel, User } from '@/payload-types'
import { cn } from '@/utilities/ui'

import { JoinButtonIntent } from './types'

import type { MembershipColorScheme } from './themes'

type JoinButtonProps = {
  tierData: MembershipLevel
  user: User | null
  userMemberLevel: MembershipLevel | null
  colorScheme: MembershipColorScheme
  onClick: (chosenPlan: MembershipLevel, intent: JoinButtonIntent) => Promise<void>
  className?: string
}

type ButtonProperties = {
  label: string
  intent: JoinButtonIntent
}

const getButtonProperties = (
  tierData: MembershipLevel,
  user: User | null,
  userMemberLevel: MembershipLevel | null,
): ButtonProperties => {
  // Free tier messages
  if (tierData.price === 0) {
    if (userMemberLevel)
      return {
        label: 'Included in Membership',
        intent: 'none',
      }
    if (user)
      return {
        label: 'Current Plan',
        intent: 'none',
      }
    return {
      label: 'Create a Free Account',
      intent: 'register',
    }
  }

  // Has plan
  if (userMemberLevel) {
    // Free tier messages
    if (tierData.price === 0)
      return {
        label: 'Included in Membership',
        intent: 'none',
      }

    // Current plan
    if (userMemberLevel.id === tierData.id)
      return {
        label: 'Current Plan',
        intent: 'none',
      }

    // Contact Us
    return {
      label: 'Contact Us',
      intent: 'contact',
    }
  }

  // Logged in
  if (user) {
    // Waitlist status
    if (user.Subscriptions?.waitlist?.waitlistMembershipType)
      return {
        label: 'Leave Waitlist',
        intent: 'leave-waitlist',
      }

    // TODO: Enter waitlist intent
  }

  return {
    label: 'Sign Up',
    intent: 'register',
  }
}

// TODO: when handling waitlist intents, we'll also need a busy state because we mark the user as waitlisted if they're logged in
export const JoinButton: React.FC<JoinButtonProps> = ({
  tierData,
  user,
  userMemberLevel,
  colorScheme,
  className,
  onClick,
}) => {
  const buttonProperties = getButtonProperties(tierData, user, userMemberLevel)

  return (
    <Button
      type="button"
      variant="accent"
      color={colorScheme.buttonColor as 'default' | 'inverse'}
      size="default"
      className={cn('text-lg font-bold', className)}
      icon={false}
      disabled={buttonProperties.intent === 'none'}
      onClick={() => {
        onClick(tierData, buttonProperties.intent)
      }}
    >
      {buttonProperties.label}
    </Button>
  )
}
